import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, NavLink, useLocation, useSearchParams } from "react-router-dom";
import { API_URL } from "../../constants/urls";
import { TechArticle, TechArticles, techCategory } from "../../interfaces";
import ArticleLink from "../ArticleLink/ArticleLink";
import { slugify } from "../../util/slugify";
import "./Editorial.css";
import Updates from "./Updates";

export const tools = [
  ["▧", "Image Compressor", "Make photos smaller with adjustable JPEG or WebP quality. Private, in your browser.", "image-compressor"],
  ["↔", "Image Resizer", "Resize images to exact dimensions, with optional aspect ratio locking.", "image-resizer"],
  ["⇄", "Image Converter", "Convert JPEG, PNG, and WebP images without uploading them.", "image-converter"],
  [
    "⇄",
    "Video Merger",
    "Join two short clips into one MP4, with audio and your choice of order.",
    "video-merger",
  ],
  [
    "TS",
    "JSON → TypeScript",
    "Turn JSON into typed interfaces. Copy or download, entirely in your browser.",
    "json-to-typescript",
  ],
  [
    "▧",
    "Number Plate Blurring",
    "Automatically blur license plates in your videos, then download the result.",
    "plate-blur",
  ],
  [
    "{ }",
    "JSON Formatter",
    "Format and validate JSON with ease.",
    "json-formatter",
  ],
  [
    "#",
    "UUID Generator",
    "Create unique identifiers in an instant.",
    "uuid-generator",
  ],
  [
    "↔",
    "Compare Text",
    "Find the differences between two texts.",
    "compare-text",
  ],
  [
    ".*",
    "Regex Generator",
    "Build patterns for your next project.",
    "regex-generator",
  ],
  [
    "QR",
    "QR Code Generator",
    "Turn your links into scannable codes.",
    "qr-code-generator",
  ],
  [
    "JWT",
    "JWT Token Generator",
    "Generate tokens for development.",
    "jwt-token-generator",
  ],
  [
    "Aa",
    "Keyword Tool",
    "Extract keywords from your content.",
    "keyword-extraction",
  ],
  [
    "↗",
    "Invoice Generator",
    "Create an invoice for your business.",
    "invoice-generator",
  ],
  [
    "N",
    "Business Name Generator",
    "Find a name for your next idea.",
    "business-name-generator",
  ],
  [
    "IV",
    "Roman Numeral Converter",
    "Convert numbers to Roman numerals.",
    "integer-roman",
  ],
  [
    "◷",
    "Time Zone Converter",
    "Find the right time, anywhere.",
    "time-zone-converter",
  ],
];

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location]);
  return (
    <header className="ed-header">
      <a className="ed-skip" href="#main-content">
        Skip to content
      </a>
      <div className="ed-masthead ed-wrap">
        <Link to="/" className="ed-logo" aria-label="Nuhman home">
          nuhman<span>.</span>
        </Link>
        <span className="ed-tagline">
          A little curiosity. A world of possibilities.
        </span>
        <Link className="ed-header-link" to="/smart-tools">
          Explore the toolkit <span aria-hidden="true">↗</span>
        </Link>
        <button
          className="ed-menu"
          aria-expanded={open}
          aria-controls="editorial-navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? "Close ✕" : "Menu ☰"}
        </button>
      </div>
      <nav
        id="editorial-navigation"
        aria-label="Main navigation"
        className={`ed-navigation ${open ? "is-open" : ""}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
      >
        <div className="ed-wrap ed-nav-inner">
          <div className="ed-nav-links">
            <NavLink to="/" end>
              Discover
            </NavLink>
            <NavLink to="/latest">Latest articles</NavLink>
            <NavLink to="/categories">Topics</NavLink>
            <NavLink to="/popular">Most read</NavLink>
            <NavLink to="/techies">Techies</NavLink>
            <NavLink to="/smart-tools">Tools & utilities</NavLink>
          </div>
          <NavLink to="/contact">
            Get in touch <span aria-hidden="true">↗</span>
          </NavLink>
        </div>
      </nav>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="ed-footer">
      <div className="ed-wrap">
        <div className="ed-footer-main">
          <div>
            <Link className="ed-logo" to="/">
              nuhman<span>.</span>
            </Link>
            <p>
              Ideas worth exploring.
              <br />
              Tools worth keeping.
            </p>
          </div>
          <div>
            <h2>Explore</h2>
            <Link to="/latest">Latest articles</Link>
            <Link to="/categories">All topics</Link>
            <Link to="/popular">Most read</Link>
          </div>
          <div>
            <h2>Make something</h2>
            <Link to="/smart-tools">Tools & utilities</Link>
            <Link to="/json-formatter">JSON Formatter</Link>
            <Link to="/contact">Get in touch</Link>
          </div>
        </div>
        <div className="ed-footer-bottom">
          <span>© {new Date().getFullYear()} Nuhman.com</span>
          <div>
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms & conditions</Link>
          </div>
          <span>Stay curious.</span>
        </div>
      </div>
    </footer>
  );
}

function plainText(value?: string) {
  const doc = new DOMParser().parseFromString(value || "", "text/html");
  return doc.body.textContent || "";
}
function DateLabel({ article }: { article: TechArticle }) {
  if (!article.createdDateTime) return null;
  const date = new Date(article.createdDateTime);
  return isNaN(date.getTime()) ? null : (
    <time dateTime={date.toISOString()}>
      {date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })}
    </time>
  );
}
function ArticleTitle({ article }: { article: TechArticle }) {
  return article.id ? (
    <ArticleLink title={article.title || "Untitled article"} id={article.id} />
  ) : (
    <>{article.title || "Untitled article"}</>
  );
}
function Category({ article }: { article: TechArticle }) {
  return article.techCategory?.id ? (
    <Link
      className="ed-eyebrow"
      to={`/categories?catid=${article.techCategory.id}&aqry=true`}
    >
      {article.techCategory.name}
    </Link>
  ) : (
    <span className="ed-eyebrow">Technology</span>
  );
}
function ArticleCard({ article }: { article: TechArticle }) {
  return (
    <article className="ed-article-card">
      <Category article={article} />
      <h3>
        <ArticleTitle article={article} />
      </h3>
      <p>{plainText(article.shortDescription || article.xshortDescription)}</p>
      <div className="ed-meta">
        <DateLabel article={article} />
        <span aria-hidden="true">↗</span>
      </div>
    </article>
  );
}
function ToolCards({ all = false }: { all?: boolean }) {
  return (
    <div className="ed-tools-grid">
      {(all ? tools : tools.slice(0, 4)).map(
        ([icon, name, description, path]) => {
          const content = (
            <>
              <span className="ed-tool-icon" aria-hidden="true">
                {icon}
              </span>
              <span className="ed-tool-arrow" aria-hidden="true">
                ↗
              </span>
              <h3>{name}</h3>
              <p>{description}</p>
            </>
          );
          return path.startsWith("https://") ? (
            <a href={path} className="ed-tool" key={path}>
              {content}
            </a>
          ) : (
            <Link to={`/${path}`} className="ed-tool" key={path}>
              {content}
            </Link>
          );
        },
      )}
    </div>
  );
}
export function Toolkit() {
  return (
    <div className="ed-wrap ed-page">
      <p className="ed-eyebrow">The toolkit</p>
      <h1>
        Less friction.
        <br />
        <em>More creating.</em>
      </h1>
      <p className="ed-intro">
        Useful little tools for the work you do every day.
      </p>
      <ToolCards all />
      <p className="ed-intro">
        Working through a coding problem?{" "}
        <Link to="/techies">Ask the Techies community ↗</Link>
      </p>
      <Updates all />
    </div>
  );
}

export default function Editorial({
  mode = "home",
}: {
  mode?: "home" | "latest" | "popular" | "categories";
}) {
  const [params] = useSearchParams();
  const categoryId = mode === "categories" ? params.get("catid") : null;
  const [categories, setCategories] = useState<techCategory[]>([]);
  const [data, setData] = useState<TechArticles>({});
  const [popular, setPopular] = useState<TechArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [categoriesError, setCategoriesError] = useState(false);
  const [page, setPage] = useState(0);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [mode, categoryId]);
  useEffect(() => {
    const controller = new AbortController();
    setCategoriesError(false);
    axios
      .get<techCategory[]>(`${API_URL}apis/nuhman/tech/categories`, {
        signal: controller.signal,
        timeout: 15000,
      })
      .then((res) => setCategories(res.data))
      .catch((err) => {
        if (!axios.isCancel(err)) setCategoriesError(true);
      });
    if (mode === "home")
      axios
        .get<TechArticles>(
          `${API_URL}apis/nuhman/tech/articles?views=1&size=4`,
          { signal: controller.signal, timeout: 15000 },
        )
        .then((res) => setPopular(res.data.techArticles || []))
        .catch(() => setPopular([]));
    return () => controller.abort();
  }, [mode, retry]);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    const query = new URLSearchParams({
      [mode === "popular" ? "views" : "created"]: "1",
      page: String(page),
      size: mode === "home" ? "7" : "12",
    });
    if (categoryId) query.set("categoryId", categoryId);
    axios
      .get<TechArticles>(`${API_URL}apis/nuhman/tech/articles?${query}`, {
        signal: controller.signal,
        timeout: 15000,
      })
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setError(true);
          setLoading(false);
        }
      });
    return () => controller.abort();
  }, [mode, categoryId, page, retry]);
  const articles = data.techArticles || [];
  const feature = articles[0];
  const category = categories.find((item) => String(item.id) === categoryId);
  const isHome = mode === "home";
  const title =
    mode === "popular"
      ? "Worth another read."
      : mode === "latest"
        ? "Fresh perspectives."
        : category?.name || "Follow your curiosity.";
  return (
    <div className="ed-wrap ed-page">
      <section className="ed-introduction">
        <div>
          <p className="ed-eyebrow">
            <span className="ed-dot" />
            Technology. Ideas. Everyday discoveries.
          </p>
          <h1>
            {isHome ? (
              <>
                For the curious.
                <br />
                <em>And the creators.</em>
              </>
            ) : (
              title
            )}
          </h1>
        </div>
        <p className="ed-intro">
          Practical guides, fresh perspectives, and useful tools.
          <br />A place to learn something new and make something better.
        </p>
      </section>
      <nav className="ed-topics" aria-label="Browse topics">
        <Link className={!categoryId ? "selected" : ""} to="/categories">
          All topics
        </Link>
        {categories.map((cat) => (
          <Link
            className={String(cat.id) === categoryId ? "selected" : ""}
            key={cat.id}
            to={`/categories?catid=${cat.id}&aqry=true`}
          >
            {cat.name}
          </Link>
        ))}
      </nav>
      {categoriesError && (
        <p className="ed-muted">
          Topics are temporarily unavailable.{" "}
          <button
            className="ed-text-button"
            onClick={() => setRetry((n) => n + 1)}
          >
            Try again
          </button>
        </p>
      )}
      {loading ? (
        <div className="ed-loading" role="status">
          <span />
          Finding your next read…
        </div>
      ) : error ? (
        <div className="ed-state" role="alert">
          <h2>We couldn’t load the articles.</h2>
          <p>
            Please try again in a moment. The toolkit is still here to explore.
          </p>
          <button className="ed-button" onClick={() => setRetry((n) => n + 1)}>
            Try again ↻
          </button>
        </div>
      ) : articles.length === 0 ? (
        <div className="ed-state">
          <h2>A new chapter is on its way.</h2>
          <p>No articles in this collection yet. Explore another topic.</p>
          <Link to="/categories">Browse all topics →</Link>
        </div>
      ) : (
        <>
          {isHome && feature && (
            <section className="ed-feature-grid" aria-label="Featured reading">
              <article className="ed-feature">
                <div className="ed-feature-art" aria-hidden="true">
                  <span className="ed-art-label">THE DEVELOPER’S NOTEBOOK</span>
                  <div className="ed-code-window">
                    <div className="ed-window-bar">
                      <i />
                      <i />
                      <i />
                      <span>hello, possibilities.</span>
                    </div>
                    <div className="ed-code">
                      <span>01</span> <b>const</b> curiosity = true;
                      <br />
                      <span>02</span>
                      <br />
                      <span>03</span> <b>while</b> (curiosity) {"{"}
                      <br />
                      <span>04</span> learn.somethingNew();
                      <br />
                      <span>05</span> build.somethingBetter();
                      <br />
                      <span>06</span> {"}"}
                    </div>
                    <span className="ed-code-cursor" />
                  </div>
                  <span className="ed-art-foot">
                    EXPLORE · UNDERSTAND · CREATE <span>↗</span>
                  </span>
                </div>
                <div className="ed-feature-copy">
                  <Category article={feature} />
                  <h2>
                    <ArticleTitle article={feature} />
                  </h2>
                  <p>{plainText(feature.shortDescription)}</p>
                  <div className="ed-meta">
                    <DateLabel article={feature} />
                    {feature.id && (
                      <a
                        href={`/#/article/${slugify(feature.title || "")}/${feature.id}`}
                        aria-label={`Read ${feature.title}`}
                      >
                        Start reading ↗
                      </a>
                    )}
                  </div>
                </div>
              </article>
              <aside className="ed-most-read">
                <div className="ed-section-heading">
                  <h2>On the reading list</h2>
                  <span className="ed-eyebrow">Most read</span>
                </div>
                {popular.length ? (
                  popular.slice(0, 4).map((article, index) => (
                    <article className="ed-ranked" key={article.id}>
                      <span className="ed-rank">0{index + 1}</span>
                      <div>
                        <Category article={article} />
                        <h3>
                          <ArticleTitle article={article} />
                        </h3>
                        <DateLabel article={article} />
                      </div>
                    </article>
                  ))
                ) : (
                  <p className="ed-muted">
                    The reading list is temporarily unavailable.
                  </p>
                )}
                <Link className="ed-more" to="/popular">
                  Explore most read <span>→</span>
                </Link>
              </aside>
            </section>
          )}
          <section className="ed-articles">
            <div className="ed-section-heading">
              <h2>
                {isHome
                  ? "The latest thinking"
                  : mode === "popular"
                    ? "Most read articles"
                    : category?.name || "Explore the articles"}
              </h2>
              {isHome ? (
                <Link to="/latest">
                  All articles <span aria-hidden="true">↗</span>
                </Link>
              ) : (
                <span className="ed-muted">
                  {data.totalElements ?? articles.length} articles
                </span>
              )}
            </div>
            <div className="ed-article-grid">
              {(isHome ? articles.slice(1) : articles).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            {!isHome && (data.totalPages || 0) > 1 && (
              <nav className="ed-pagination" aria-label="Article pages">
                <button
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  ← Previous
                </button>
                <span>
                  Page {page + 1} of {data.totalPages}
                </span>
                <button
                  disabled={page + 1 >= (data.totalPages || 1)}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next →
                </button>
              </nav>
            )}
          </section>
        </>
      )}
      {isHome && <Updates />}
      <section className="ed-toolkit">
        <div className="ed-section-heading">
          <div>
            <p className="ed-eyebrow">Small tools. Big difference.</p>
            <h2>A little help for your next big idea.</h2>
          </div>
          <Link to="/smart-tools">All tools ↗</Link>
        </div>
        <ToolCards />
      </section>
      <section className="ed-closing">
        <span className="ed-asterisk" aria-hidden="true">
          ✳
        </span>
        <div>
          <p className="ed-eyebrow">Keep exploring</p>
          <h2>
            Your next idea starts
            <br />
            with a little curiosity.
          </h2>
        </div>
        <Link className="ed-button" to="/categories">
          Find your next read <span>↗</span>
        </Link>
      </section>
    </div>
  );
}
